import { Home, LogIn } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { commonLabels } from "@/i18n/labels/commonLabels";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

type Props = {
  /** Mobile / native: icon-only controls */
  compact?: boolean;
  /** Extra classes for the home / login buttons (theme variants) */
  buttonClassName?: string;
};

/**
 * Guest replacement for the logged-in Dashboard control on /auctions.
 * Web: home → landing, login → /auth. Native: login only (home is bottom nav).
 */
export function AuctionsGuestHeaderHome({
  compact = true,
  buttonClassName,
}: Props) {
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const menu = commonLabels(language).menu;

  if (isNative) {
    return <span className="inline-block h-9 w-9" aria-hidden />;
  }

  return (
    <BtnNavigateBack
      label={menu?.home ?? "Hem"}
      to="/"
      iconOnly={compact}
      icon={Home}
      className={cn(
        "mb-0 h-9 rounded-xl text-sm font-medium border-0",
        compact
          ? "w-9 px-0 justify-center text-foreground/90 bg-black/[0.05] ring-1 ring-black/10 hover:bg-black/[0.08]"
          : "px-2.5",
        buttonClassName,
      )}
    />
  );
}

export function AuctionsGuestHeaderLogin({
  compact = true,
  buttonClassName,
}: Props) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const menu = commonLabels(language).menu;
  const loginLabel = menu?.login ?? "Logga in";

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        "h-9 rounded-xl text-sm font-medium border-0",
        compact
          ? "w-9 px-0 justify-center text-foreground/90 bg-black/[0.05] ring-1 ring-black/10 hover:bg-black/[0.08]"
          : "px-2.5",
        buttonClassName,
      )}
      onClick={() => navigate("/auth")}
      aria-label={loginLabel}
    >
      {compact ? <LogIn className="h-4 w-4" strokeWidth={2.25} /> : loginLabel}
    </Button>
  );
}
